// §5.2 — **the drag rule** (D-034 rule 1, binding).
//
// While a pointer is down, authoritative geometry in the store is *not* updated. Every
// `pointermove` writes `transform` (and `width`/`height` when resizing) straight onto the one
// window element, and a single commit lands in the store on `pointerup`. Otherwise dragging a
// window with eight apps open re-renders the tree at pointer rate.
//
// The DOM types here are narrow structural interfaces rather than `HTMLElement`/`PointerEvent`
// so the whole session is testable without a browser — a real element and a real pointer event
// both satisfy them.
import type { Edge, Rect } from '@hangar/shared';

export interface DragStyle {
  transform: string;
  width: string;
  height: string;
}

export interface DragElement {
  style: DragStyle;
  setPointerCapture?(pointerId: number): void;
  releasePointerCapture?(pointerId: number): void;
}

export interface PointerLike {
  pointerId: number;
  clientX: number;
  clientY: number;
  getCoalescedEvents?(): PointerLike[];
}

export type DragKind = { kind: 'move' } | { kind: 'resize'; edge: Edge };

export const MOVE: DragKind = { kind: 'move' };
export const resizeFrom = (edge: Edge): DragKind => ({ kind: 'resize', edge });

/** The eight handles a skin renders (§5.2). */
export const RESIZE_EDGES: readonly Edge[] = [
  'top',
  'right',
  'bottom',
  'left',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
];

const AFFECTS_LEFT: ReadonlySet<Edge> = new Set(['left', 'topLeft', 'bottomLeft']);
const AFFECTS_RIGHT: ReadonlySet<Edge> = new Set(['right', 'topRight', 'bottomRight']);
const AFFECTS_TOP: ReadonlySet<Edge> = new Set(['top', 'topLeft', 'topRight']);
const AFFECTS_BOTTOM: ReadonlySet<Edge> = new Set(['bottom', 'bottomLeft', 'bottomRight']);

export interface MinSize {
  w: number;
  h: number;
}

/** Pure: the rect a move of (dx, dy) produces. */
export function applyMove(start: Rect, dx: number, dy: number): Rect {
  return { x: start.x + dx, y: start.y + dy, w: start.w, h: start.h };
}

/**
 * Pure: the rect a resize of (dx, dy) on `edge` produces. A left or top edge moves the origin
 * as well as the size, and the minimum size pins the moving edge rather than the anchored one.
 */
export function applyResize(start: Rect, edge: Edge, dx: number, dy: number, min: MinSize): Rect {
  let { x, y, w, h } = start;

  if (AFFECTS_RIGHT.has(edge)) w = Math.max(min.w, start.w + dx);
  if (AFFECTS_BOTTOM.has(edge)) h = Math.max(min.h, start.h + dy);
  if (AFFECTS_LEFT.has(edge)) {
    w = Math.max(min.w, start.w - dx);
    x = start.x + (start.w - w);
  }
  if (AFFECTS_TOP.has(edge)) {
    h = Math.max(min.h, start.h - dy);
    y = start.y + (start.h - h);
  }

  return { x, y, w, h };
}

export interface DragSessionOptions {
  /** The element whose `transform`/`width`/`height` the session writes. Usually `.win-host`. */
  element: DragElement;
  /**
   * The element that takes pointer capture — the grab handle, not the window. Once a pointer is
   * captured, events are dispatched to the capturing element itself, so capturing on the window
   * would stop the handle's own listeners from ever firing again. Defaults to `element`.
   */
  capture?: DragElement;
  /** Geometry at `pointerdown`, in desktop space. */
  start: Rect;
  origin: { clientX: number; clientY: number };
  pointerId: number;
  kind: DragKind;
  min?: MinSize;
  /**
   * Confines the result; omit to leave the window free.
   *
   * A **move** is held wholly inside it. A **resize** only has its top and left edges held there
   * (D-438), because a window may be made larger than the screen but its title bar may never be
   * put where nobody can reach it.
   */
  bounds?: Rect;
  /**
   * Called on every applied frame with the live rect. Used by the snap overlay (§5.2) — which
   * also draws directly and never goes through state.
   */
  onPreview?: (rect: Rect) => void;
  /** Called exactly once, on `pointerup`. This is the only store write of the whole drag. */
  onCommit: (rect: Rect) => void;
  /** Called instead of `onCommit` when the drag is cancelled. */
  onCancel?: () => void;
  /** Frame scheduler. Defaults to `requestAnimationFrame`; tests inject a synchronous one. */
  schedule?: (cb: () => void) => void;
}

export interface DragSession {
  readonly active: boolean;
  /** The rect the session would commit right now. */
  current(): Rect;
  pointerMove(event: PointerLike): void;
  pointerUp(event?: PointerLike): void;
  cancel(): void;
}

function defaultSchedule(cb: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => cb());
  else cb();
}

/**
 * Hold a rect inside `bounds` — **origin last**, which is the whole of the fix for D-438.
 *
 * The obvious order is wrong for a window bigger than the work area. `min(max(y, top), bottom - h)`
 * evaluates `bottom - h` to something *above* `top` when `h > bounds.h`, so the outer `min` wins and
 * the window is pinned with its title bar above the top of the screen — which is a window that can
 * no longer be dragged or closed, because the only handle for either is up there with it. Applying
 * the lower bound last means an oversized window hangs off the **bottom right**, where every control
 * it has is still on screen.
 *
 * A window can become oversized without ever being resized: open it at 820 px tall, then make the
 * browser window shorter. Nothing moves at that moment — it is the next drag that used to throw the
 * title bar away.
 */
export function confine(rect: Rect, bounds: Rect | undefined): Rect {
  if (!bounds) return rect;
  return {
    ...rect,
    x: Math.max(bounds.x, Math.min(rect.x, bounds.x + bounds.w - rect.w)),
    y: Math.max(bounds.y, Math.min(rect.y, bounds.y + bounds.h - rect.h)),
  };
}

/**
 * The same guarantee for a **resize**: the top and left edges stop at the work area (D-438).
 *
 * A resize is not confined the way a move is — a window may legitimately be made larger than the
 * screen, so the bottom and right edges stay free. What may not happen is the *top* edge going
 * above the top of the screen, because the title bar goes with it. So the origin is pulled back and
 * the size is reduced by the same amount, which is the edge stopping rather than the window
 * jumping.
 *
 * This path is reachable precisely because the pointer is captured: once it is, dragging out of the
 * browser window keeps delivering events, with negative `clientY`.
 */
function confineResize(rect: Rect, bounds: Rect | undefined, min: MinSize): Rect {
  if (!bounds) return rect;
  let { x, y, w, h } = rect;
  if (x < bounds.x) {
    w = Math.max(min.w, w - (bounds.x - x));
    x = bounds.x;
  }
  if (y < bounds.y) {
    h = Math.max(min.h, h - (bounds.y - y));
    y = bounds.y;
  }
  return { x, y, w, h };
}

export function beginDrag(options: DragSessionOptions): DragSession {
  const { element, start, origin, pointerId, kind, onCommit } = options;
  const min = options.min ?? { w: 0, h: 0 };
  const schedule = options.schedule ?? defaultSchedule;
  const capture = options.capture ?? element;

  let active = true;
  let rect: Rect = { ...start };
  let pending: { dx: number; dy: number } | null = null;
  let frameQueued = false;

  capture.setPointerCapture?.(pointerId);

  const write = (): void => {
    frameQueued = false;
    const delta = pending;
    pending = null;
    if (!active || !delta) return;

    rect =
      kind.kind === 'move'
        ? confine(applyMove(start, delta.dx, delta.dy), options.bounds)
        : confineResize(applyResize(start, kind.edge, delta.dx, delta.dy, min), options.bounds, min);

    // The whole point of §5.2: straight to the DOM, never through React state.
    element.style.transform = `translate3d(${rect.x}px, ${rect.y}px, 0)`;
    if (kind.kind === 'resize') {
      element.style.width = `${rect.w}px`;
      element.style.height = `${rect.h}px`;
    }
    options.onPreview?.(rect);
  };

  const finish = (): void => {
    active = false;
    capture.releasePointerCapture?.(pointerId);
  };

  const pointerMove = (event: PointerLike): void => {
    if (!active) return;
    // High-rate pointers deliver several samples per frame; only the last one matters.
    const samples = event.getCoalescedEvents?.() ?? [];
    const last = samples.length > 0 ? (samples[samples.length - 1] ?? event) : event;
    pending = { dx: last.clientX - origin.clientX, dy: last.clientY - origin.clientY };

    if (frameQueued) return;
    frameQueued = true;
    schedule(write);
  };

  return {
    get active() {
      return active;
    },

    current() {
      return { ...rect };
    },

    pointerMove,

    pointerUp(event) {
      if (!active) return;
      if (event) pointerMove(event);
      // Apply anything still queued so the commit matches what the user last saw.
      if (pending) write();
      finish();
      onCommit({ ...rect });
    },

    cancel() {
      if (!active) return;
      pending = null;
      finish();
      element.style.transform = `translate3d(${start.x}px, ${start.y}px, 0)`;
      if (kind.kind === 'resize') {
        element.style.width = `${start.w}px`;
        element.style.height = `${start.h}px`;
      }
      options.onCancel?.();
    },
  };
}
